import type { ReactNode } from "react";
import type { GameState } from "src/core/types";
import type { Theme } from "src/theme";
import { useContext, useEffect, useRef } from "react";
import Board from "./Board";
import { type PlayAction } from "src/reducer";
import { isInNamuaStage, opponent, COMPUTER_SIDE, playerWon, playerLost } from "src/core/baoside";
import { alphaBeta, KISWAHILI } from "src/core/baogame";
import GameSettingsContext from "src/components/providers/GameSettingsProvider";
import GameDispatchContext from "src/components/providers/GameDispatchProvider";
import GameReducerStateContext from "src/components/providers/GameReducerStateProvider";
import GameOverModal from "../GameOverModal";

import OrientationContext, { PORTRAIT, type Orientation } from "src/components/providers/OrientationProvider";
import { logGameLost, logGameStarted, logGameWon } from "../../hooks/use-analytics";
import { useGameHistory } from "src/hooks/useGameHistory";

type Props = {
  path: string;
};

const searchDepth = {
  beginner: 1,
  intermediate: 2,
  expert: 5,
  multiplayer: 0 // no AI
};
const PLAY_SWAP_INTERVAL = 1500;
const isStartingState = (gameState: GameState) =>
  gameState[0].every((d: number) => d === 2) && gameState[1].every((d: number) => d === 2);

const BoardContainer = (props: Props): ReactNode => {
  // Consume GameSettingsContext from App.js
  const gameSettingsContext = useContext(GameSettingsContext);
  const settings = gameSettingsContext.settings;
  const gameType = gameSettingsContext.gameType;
  const setGameStage = gameSettingsContext.setGameStage;

  const orientation = useContext<Orientation>(OrientationContext);

  // Consume game state and dispatch from App.js contexts
  const state = useContext(GameReducerStateContext);
  const gameDispatchContext = useContext(GameDispatchContext);
  const dispatch = gameDispatchContext.dispatch;

  // Game history tracking
  const { addGameToHistory } = useGameHistory();
  const gameStartTime = useRef<number>(Date.now());

  const currentPlayer = state.currentPlayer;
  const gameState = state.currentGameState;
  const currentSequence = state.currentSequence[state.currentSequenceIndex];
  const currentSequenceHoleIndex = currentSequence ? currentSequence.index : -1;

  const activeIndex = currentSequenceHoleIndex;
  const endSequence =
    state.currentSequence.length > 0 && state.currentSequenceIndex === state.currentSequence.length - 1;

  const won = playerWon(gameState);
  const lost = playerLost(gameState);
  const gameOver = endSequence && (won || lost);
  const playbackSpeed = settings.playbackSpeed;
  const currentSequenceSide = currentSequence ? currentSequence.side : currentPlayer;

  const boardContainerStyle = (theme: Theme) => {
    let boardWidth = (theme.holeWidth + 16) * 8 + 10;
    let boardHeight = (theme.holeHeight + 16) * 4 + 10;

    const xOffset = gameType === KISWAHILI ? 35 : 0;
    return orientation === PORTRAIT
      ? {
          transformOrigin: "top left",
          transform: `translate(${boardHeight + xOffset}px) rotate(90deg)`,
          height: boardHeight,
          width: boardWidth
        }
      : {};
  };

  useEffect(() => {
    if (setGameStage) {
      setGameStage(gameState && isInNamuaStage(gameState) ? "namua" : "mtaji");
    }
  }, [gameState, setGameStage]);

  useEffect(() => {
    if (activeIndex !== -1 && !endSequence) {
      const id = setInterval(() => {
        dispatch({ type: "SEQUENCE_STEP_FORWARD" });
      }, playbackSpeed);
      return () => clearInterval(id);
    } else if (endSequence && !gameOver) {
      const id = setTimeout(() => {
        dispatch({
          type: "CHANGE_PLAYER",
          side: opponent(currentPlayer)
        });
      }, PLAY_SWAP_INTERVAL);
      return () => clearTimeout(id);
    }
  }, [activeIndex, currentPlayer, dispatch, endSequence, gameOver, playbackSpeed]);

  useEffect(() => {
    if (!gameOver) {
      return;
    }
    const numberOfTurns = state.moveHistory.length;
    const gameAttributes = {
      numberOfTurns,
      startingSide: settings.startingSide,
      gameType,
      expertLevel: settings.expertLevel
    };

    const result = won ? "won" : "lost";

    if (won) {
      logGameWon(gameAttributes);
    } else {
      logGameLost(gameAttributes);
    }

    // Save to game history
    const gameEndTime = Date.now();
    const gameId = `single_${gameStartTime.current}_${gameEndTime}`;
    addGameToHistory({
      gameId,
      gameType,
      mode: "single",
      result,
      opponent: "AI",
      numberOfTurns,
      startingSide: settings.startingSide,
      expertLevel: settings.expertLevel,
      duration: gameEndTime - gameStartTime.current
    }).catch((err) => {
      console.error("Failed to save game to history:", err);
    });
  }, [gameOver, settings.expertLevel, settings.startingSide, state.moveHistory, gameType, won, addGameToHistory]);

  useEffect(() => {
    if (
      (gameOver && state.currentSequenceIndex === state.currentSequence.length - 1) ||
      state.thinking ||
      currentPlayer !== COMPUTER_SIDE ||
      state.currentSequenceIndex !== -1
    ) {
      return;
    }
    dispatch({ type: "THINKING" });
    const adjustedDepth = isStartingState(gameState) ? 1 : searchDepth[settings.expertLevel] || 1;

    setTimeout(() => {
      const [bestMove] = alphaBeta(gameState, currentPlayer, adjustedDepth);
      if (bestMove) {
        const action: PlayAction = {
          type: "USER_PLAY",
          index: bestMove.index,
          direction: bestMove.direction,
          side: currentPlayer
        };
        setTimeout(() => {
          dispatch(action);
        }, PLAY_SWAP_INTERVAL);
      } else {
        console.log("problem getting state");
        console.log(gameState, currentPlayer, adjustedDepth);
      }
    }, 500);
  });

  useEffect(() => {
    logGameStarted({ gameType, startingSide: settings.startingSide, expertLevel: settings.expertLevel });
  }, [settings.startingSide, gameType, settings.expertLevel]);

  return (
    <div css={boardContainerStyle} data-testid="board-container">
      <Board
        dispatch={dispatch}
        gameType={gameType}
        store={state.store}
        startingSide={settings.startingSide}
        gameState={gameState}
        activeSide={currentSequenceSide}
        currentPlayer={currentPlayer}
        activeIndex={activeIndex}
        idle={endSequence || state.currentSequenceIndex === -1}
      >
        {gameOver ? <GameOverModal won={won} /> : null}
      </Board>
    </div>
  );
};

export default BoardContainer;
